import * as React from "react";
import {
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Alert,
  Box,
  TableContainer,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { adminFetch } from "../../services/adminFetch";

type Row = any;

function todayPlus(days: number) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function toNum(v: any) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function hasRequiredForAvailability(r: any) {
  return r?.persons_max != null;
}

function scoreRow(r: any, guests: number) {
  const minP = toNum(r.persons_min);
  const maxP = toNum(r.persons_max);
  if (maxP == null) return -999999;

  // soft validity (αν και έχεις ήδη φιλτράρει)
  if (minP != null && guests < minP) return -999999;
  if (guests > maxP) return -999999;

  const slack = maxP - guests; // 0 καλύτερο
  const capacityScore = 100 - Math.min(100, slack * 10);

  const bonus =
    (r.featured_image_url ? 8 : 0) +
    (r.latitude != null && r.longitude != null ? 3 : 0) +
    (r.city ? 2 : 0) +
    (r.region ? 2 : 0);

  return capacityScore + bonus;
}

function clamp(n: number, a = 0, b = 100) {
  return Math.max(a, Math.min(b, n));
}

function normalizePercent(score: number, minS: number, maxS: number) {
  if (!Number.isFinite(score)) return 0;
  if (maxS <= minS) return 100;
  return clamp(((score - minS) / (maxS - minS)) * 100);
}

function percentColor(p: number): "success" | "warning" | "error" {
  if (p >= 85) return "success";
  if (p >= 60) return "warning";
  return "error";
}

export default function AdminAvailabilityPage() {
  const nav = useNavigate();
  const params = useParams() as { locale?: string };
  const locale = params?.locale || "el";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [checkIn, setCheckIn] = React.useState(todayPlus(1));
  const [checkOut, setCheckOut] = React.useState(todayPlus(6));
  const [guests, setGuests] = React.useState<number>(2);
  const [maxPrice, setMaxPrice] = React.useState<string>("");

  const [items, setItems] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const maxPriceNum = maxPrice.trim() ? Number(maxPrice) : null;

  const search = async () => {
    setErr(null);
    setLoading(true);
    try {
      const payload = {
        check_in: checkIn,
        check_out: checkOut,
        guests_total: Number(guests),
        max_price: maxPrice.trim() ? Number(maxPrice) : null,
      };

      const res = await adminFetch("/api/admin/availability/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data?.error || "Αποτυχία αναζήτησης.");
        setItems([]);
        return;
      }
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setErr("Αποτυχία αναζήτησης.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { ranked, incomplete } = React.useMemo(() => {
    const complete = items.filter(hasRequiredForAvailability);
    const incomplete = items.filter((x) => !hasRequiredForAvailability(x));

    if (!complete.length) {
      return { ranked: [], incomplete };
    }

    const scored = complete.map((r) => ({
      r,
      s: scoreRow(r, guests),
    }));

    const scores = scored.map((x) => x.s);
    const minS = Math.min(...scores);
    const maxS = Math.max(...scores);

    const ranked = scored
      .sort((a, b) => b.s - a.s)
      .map((x, idx) => ({
        ...x.r,
        _rank: idx + 1,
        _score: x.s,
        _matchPercent: normalizePercent(x.s, minS, maxS),
      }));

    return { ranked, incomplete };
  }, [items, guests, maxPriceNum]);

  const best = ranked[0];

  return (
    <Stack spacing={2}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={2}
      >
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          Admin · Availability
        </Typography>

        <Button
          fullWidth={isMobile}
          variant="outlined"
          onClick={() => nav(`/${locale}/admin/properties`)}
        >
          Properties
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        {/* Filters */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
            alignItems: "end",
          }}
        >
          <TextField
            size="small"
            label="Check-in"
            type="date"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Check-out"
            type="date"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Guests"
            type="number"
            value={guests}
            onChange={(e) => setGuests(Number(e.target.value || 1))}
            inputProps={{ min: 1, max: 50 }}
          />
          {/* <TextField
            size="small"
            label="Max €/night"
            type="number"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
          /> */}

          <Button
            variant="contained"
            onClick={search}
            disabled={loading}
            sx={{ fontWeight: 900 }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Box>

        {err ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        ) : null}

        {/* Best match card */}
        {best ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "action.hover",
                borderColor: "success.main",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ md: "center" }}
                gap={2}
              >
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Chip
                      size="small"
                      label={`Best match · ${Math.round(best._matchPercent)}%`}
                      color="success"
                    />
                    {/* <Chip
                      size="small"
                      label={`${best.price_per_night}€ / night`}
                      variant="outlined"
                    /> */}
                    <Chip
                      size="small"
                      label={`Max persons: ${best.persons_max}`}
                      variant="outlined"
                    />
                  </Stack>

                  <Typography sx={{ fontWeight: 900, mt: 1 }}>
                    {best.display_title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ wordBreak: "break-word" }}
                  >
                    #{best.id} · {best.slug} ·{" "}
                    {[best.city, best.region].filter(Boolean).join(", ") || "-"}
                  </Typography>
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    fullWidth={isMobile}
                    variant="outlined"
                    onClick={() => nav(`/${locale}/admin/properties/${best.id}`)}
                  >
                    Edit
                  </Button>
                  <Button
                    fullWidth={isMobile}
                    variant="contained"
                    onClick={() => nav(`/${locale}/properties/${best.slug}`)}
                    sx={{ fontWeight: 900 }}
                  >
                    View
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </>
        ) : null}

        <Divider sx={{ my: 2 }} />

        {/* Ranked */}
        <Typography sx={{ fontWeight: 900, mb: 1 }}>Ranked results</Typography>

        {isMobile ? (
          <Stack spacing={1.5}>
            {ranked.map((r: any) => {
              const p = r._matchPercent ?? 0;
              const c = percentColor(p);
              const area = [r.city, r.region].filter(Boolean).join(", ") || "-";

              return (
                <Card
                  key={r.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    bgcolor: r._rank === 1 ? "action.hover" : "transparent",
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <Chip size="small" label={`#${r._rank}`} />
                        <Chip size="small" label={`${Math.round(p)}%`} color={c} />
                        {/* <Chip size="small" label={`${r.price_per_night}€`} variant="outlined" /> */}
                        <Chip
                          size="small"
                          label={`Persons: ${r.persons_min ?? "-"}–${r.persons_max ?? "-"}`}
                          variant="outlined"
                        />
                      </Stack>

                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>
                          {r.display_title || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{r.id} · {r.slug}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {area}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          onClick={() => nav(`/${locale}/admin/properties/${r.id}`)}
                        >
                          Edit
                        </Button>
                        <Button
                          fullWidth
                          size="small"
                          variant="text"
                          onClick={() => nav(`/${locale}/properties/${r.slug}`)}
                        >
                          View
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}

            {!ranked.length ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Typography color="text.secondary">
                  Δεν βρέθηκαν διαθέσιμα ακίνητα με αυτά τα κριτήρια.
                </Typography>
              </Paper>
            ) : null}
          </Stack>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Ακίνητο</TableCell>
                  <TableCell>Περιοχή</TableCell>
                  <TableCell>Persons</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ranked.map((r: any) => {
                  const p = r._matchPercent ?? 0;
                  const c = percentColor(p);

                  return (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{
                        bgcolor: r._rank === 1 ? "action.hover" : "transparent",
                      }}
                    >
                      <TableCell sx={{ width: 60 }}>
                        <Chip size="small" label={`#${r._rank}`} />
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontWeight: 900 }}>{r.display_title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{r.id} · {r.slug}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {[r.city, r.region].filter(Boolean).join(", ") || "-"}
                      </TableCell>

                      <TableCell>
                        {r.persons_min ?? "-"}–{r.persons_max ?? "-"}
                      </TableCell>

                      {/* <TableCell>
                        <Chip size="small" label={`${r.price_per_night}€`} variant="outlined" />
                      </TableCell> */}

                      <TableCell>
                        <Chip size="small" label={`${Math.round(p)}%`} color={c} />
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            sx={{ flex: 1, minWidth: 0 }}
                            onClick={() => nav(`/${locale}/admin/properties/${r.id}`)}
                          >
                            Edit
                          </Button>
                          <Button
                            fullWidth
                            size="small"
                            variant="contained"
                            sx={{ flex: 1, minWidth: 0, fontWeight: 800 }}
                            onClick={() => nav(`/${locale}/properties/${r.slug}`)}
                          >
                            View
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!ranked.length ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary">
                        Δεν βρέθηκαν διαθέσιμα ακίνητα με αυτά τα κριτήρια.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Incomplete */}
        {incomplete.length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontWeight: 900 }}>
              Incomplete listings (χρειάζονται συμπλήρωση)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Αυτά δεν μπαίνουν στο ranking γιατί λείπει persons_max.
            </Typography>

            {isMobile ? (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {incomplete.map((r: any) => {
                  const missing = [
                    r.persons_max == null ? "persons_max" : null,
                  ].filter(Boolean) as string[];

                  return (
                    <Card key={`inc-${r.id}`} variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Stack spacing={1}>
                          <Box>
                            <Typography sx={{ fontWeight: 900 }}>
                              {r.display_title || "-"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              #{r.id} · {r.slug}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {missing.map((m) => (
                              <Chip key={m} size="small" label={m} color="warning" />
                            ))}
                          </Stack>

                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            onClick={() => nav(`/${locale}/admin/properties/${r.id}`)}
                          >
                            Fix
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <TableContainer sx={{ overflowX: "auto", mt: 1 }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ακίνητο</TableCell>
                      <TableCell>Λείπει</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {incomplete.map((r: any) => {
                      const missing = [
                        r.persons_max == null ? "persons_max" : null,
                      ].filter(Boolean);

                      return (
                        <TableRow key={`inc-${r.id}`} hover>
                          <TableCell>
                            <Typography sx={{ fontWeight: 800 }}>{r.display_title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              #{r.id} · {r.slug}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              {missing.map((m) => (
                                <Chip
                                  key={m as string}
                                  size="small"
                                  label={m as string}
                                  color="warning"
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => nav(`/${locale}/admin/properties/${r.id}`)}
                            >
                              Fix
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}
      </Paper>
    </Stack>
  );
}
